import { useLazyQuery } from '@apollo/client';
import type { TariffEvaluatorOutput as SharedTariffEvaluatorOutput } from '@xbcb/api-gateway-client';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';
import type { NamePath } from '@xbcb/ui-types';
import {
  evaluateTariffsDocument,
  ModeOfTransport,
  type TariffEvaluatorInput,
} from '__generated__/graphql';
import { FormInstance } from 'antd';
import { isTariff232AutomationFeatureEnabled } from 'featureFlags';
import { applyTariffEvaluationToFormLine } from 'libs/applyTariffEvaluation';
import { useCallback, useMemo, useRef, useState } from 'react';

interface UseTariffEvaluationArgs {
  form: FormInstance;
  tariffFullNamePath: NamePath;
  originCountryCode: string | undefined;
  arrivalDate: string | undefined;
  modeOfTransport: ModeOfTransport | undefined;
}

export const useTariffEvaluation = ({
  form,
  tariffFullNamePath,
  originCountryCode,
  arrivalDate,
  modeOfTransport,
}: UseTariffEvaluationArgs) => {
  const [evaluateTariffs] = useLazyQuery(evaluateTariffsDocument);
  const [evaluating, setEvaluating] = useState(false);

  const { lineFullNamePath, productFullNamePath, linesPath, lineIndex } =
    useMemo(() => {
      const path = tariffFullNamePath as (string | number)[];
      const linePath = path.slice(0, -1);
      const linesIdx = path.lastIndexOf('lines');
      const linesArrayPath =
        linesIdx >= 0 ? path.slice(0, linesIdx + 1) : linePath;
      const productPath = linesIdx >= 0 ? path.slice(0, linesIdx) : linePath;
      const idx = linesIdx >= 0 ? (path[linesIdx + 1] as number) : 0;
      return {
        lineFullNamePath: linePath,
        productFullNamePath: productPath,
        linesPath: linesArrayPath,
        lineIndex: idx,
      };
    }, [tariffFullNamePath]);

  const argsRef = useRef({ originCountryCode, arrivalDate, modeOfTransport });
  argsRef.current = { originCountryCode, arrivalDate, modeOfTransport };
  const inflightRef = useRef(false);

  const runTariffEvaluation = useCallback(
    async (htsCode: string) => {
      const { originCountryCode, arrivalDate, modeOfTransport } =
        argsRef.current;
      if (!isRealHts(htsCode) || !originCountryCode) return;
      if (inflightRef.current) return;

      const line = form.getFieldValue(lineFullNamePath) ?? {};
      const product = form.getFieldValue(productFullNamePath) ?? {};
      const transport =
        arrivalDate || modeOfTransport
          ? {
              ...(arrivalDate && { arrival: { importDate: arrivalDate } }),
              ...(modeOfTransport && { modeOfTransport }),
            }
          : undefined;

      setEvaluating(true);
      inflightRef.current = true;
      try {
        // The returned lines already carry the evaluator's
        // `candidateAdditionalTariffs` (an empty array when the engine returns
        // none), so applying a line both updates its stacking and reconciles
        // its candidate list in a single write.
        const newLines = await applyTariffEvaluationToFormLine({
          line,
          product,
          transport,
          evaluate: async (input) => {
            const { data } = await evaluateTariffs({
              variables: { input: input as unknown as TariffEvaluatorInput },
            });
            return data?.evaluateTariffs as
              | SharedTariffEvaluatorOutput
              | null
              | undefined;
          },
        });

        if (!newLines?.length) return;

        // Every returned line carries the engine's `candidateAdditionalTariffs`
        // (empty when none apply, and identical across split lines). When the
        // 232 automation feature is on and candidates remain, the
        // product-usage questions are unanswered and the engine's stacking
        // isn't final yet — including any line split. In that case keep the
        // single original line and only refresh its candidate list (which
        // drives the Product Usage tab) rather than applying the tentative
        // restructure. Once candidates come back empty — and always when the
        // feature is off — apply the engine's full result.
        const pendingCandidates = newLines[0].candidateAdditionalTariffs;
        if (isTariff232AutomationFeatureEnabled && pendingCandidates?.length) {
          form.setFieldValue(lineFullNamePath, {
            ...line,
            candidateAdditionalTariffs: pendingCandidates,
          });
          return;
        }

        if (newLines.length === 1) {
          form.setFieldValue(lineFullNamePath, newLines[0]);
        } else {
          const allLines =
            (form.getFieldValue(linesPath) as unknown[] | undefined) ?? [];
          form.setFieldValue(linesPath, [
            ...allLines.slice(0, lineIndex),
            ...newLines,
            ...allLines.slice(lineIndex + 1),
          ]);
        }
      } catch {
        // Tariff evaluation failed — additional tariffs won't auto-populate.
      } finally {
        inflightRef.current = false;
        setEvaluating(false);
      }
    },
    [
      evaluateTariffs,
      form,
      lineFullNamePath,
      productFullNamePath,
      linesPath,
      lineIndex,
    ],
  );

  return { runTariffEvaluation, evaluating };
};
